import { PROTOCOL_VERSION } from '../../../shared/protocol';

export interface RtcCallbacks {
  signal(peerId: string, kind: 'offer' | 'answer' | 'candidate', data: unknown): void;
  data(peerId: string, data: string | ArrayBuffer): void;
  path(peerId: string, path: 'direct' | 'turn' | 'connecting'): void;
  closed(peerId: string): void;
}

interface PeerRtc {
  pc: RTCPeerConnection;
  channel?: RTCDataChannel;
  pendingCandidates: RTCIceCandidateInit[];
  remoteDescription: boolean;
  makingOffer: boolean;
  heartbeatTimer?: number;
}

export class RtcManager {
  private readonly peers = new Map<string, PeerRtc>();
  private iceServers: RTCIceServer[] = [];

  constructor(private readonly callbacks: RtcCallbacks) {}

  setIceServers(value: unknown): void {
    this.iceServers = Array.isArray(value) ? (value as RTCIceServer[]) : [];
  }

  async ensure(peerId: string, initiator: boolean): Promise<void> {
    if (!('RTCPeerConnection' in window)) return;
    let peer = this.peers.get(peerId);
    if (!peer) {
      const pc = new RTCPeerConnection({ iceServers: this.iceServers });
      peer = {
        pc,
        pendingCandidates: [],
        remoteDescription: false,
        makingOffer: false,
      };
      this.peers.set(peerId, peer);

      pc.onicecandidate = (event): void => {
        if (event.candidate) {
          this.callbacks.signal(peerId, 'candidate', event.candidate.toJSON());
        }
      };

      pc.onconnectionstatechange = (): void => {
        if (pc.connectionState === 'connected') {
          this.callbacks.path(peerId, 'direct');
        }
        if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
          this.callbacks.closed(peerId);
        }
      };

      pc.ondatachannel = (event): void => {
        this.attachChannel(peerId, peer!, event.channel);
      };

      if (initiator) {
        this.attachChannel(peerId, peer, pc.createDataChannel('drop', { ordered: true }));
      }
    }

    if (initiator && !peer.makingOffer && !peer.remoteDescription) {
      peer.makingOffer = true;
      try {
        await peer.pc.setLocalDescription(await peer.pc.createOffer());
        this.callbacks.signal(peerId, 'offer', peer.pc.localDescription);
      } finally {
        peer.makingOffer = false;
      }
    }
  }

  async signal(
    peerId: string,
    kind: 'offer' | 'answer' | 'candidate',
    data: unknown,
  ): Promise<void> {
    await this.ensure(peerId, false);
    const peer = this.peers.get(peerId);
    if (!peer) return;

    try {
      if (kind === 'offer') {
        await peer.pc.setRemoteDescription(data as RTCSessionDescriptionInit);
        peer.remoteDescription = true;
        for (const candidate of peer.pendingCandidates) {
          await peer.pc.addIceCandidate(candidate);
        }
        peer.pendingCandidates = [];
        await peer.pc.setLocalDescription(await peer.pc.createAnswer());
        this.callbacks.signal(peerId, 'answer', peer.pc.localDescription);
      } else if (kind === 'answer') {
        await peer.pc.setRemoteDescription(data as RTCSessionDescriptionInit);
        peer.remoteDescription = true;
        for (const candidate of peer.pendingCandidates) {
          await peer.pc.addIceCandidate(candidate);
        }
        peer.pendingCandidates = [];
      } else {
        const candidate = data as RTCIceCandidateInit;
        if (peer.remoteDescription) {
          await peer.pc.addIceCandidate(candidate);
        } else {
          peer.pendingCandidates.push(candidate);
        }
      }
    } catch {
      this.callbacks.closed(peerId);
    }
  }

  send(peerId: string, value: string | ArrayBuffer | Uint8Array): boolean {
    const channel = this.peers.get(peerId)?.channel;
    if (!channel || channel.readyState !== 'open') return false;
    if (typeof value === 'string') {
      channel.send(value);
    } else {
      channel.send(
        new Uint8Array(
          value instanceof ArrayBuffer ? value : (value.buffer as ArrayBuffer),
        ),
      );
    }
    return true;
  }

  isOpen(peerId: string): boolean {
    return this.peers.get(peerId)?.channel?.readyState === 'open';
  }

  bufferedAmount(peerId: string): number {
    return this.peers.get(peerId)?.channel?.bufferedAmount ?? 0;
  }

  waitOpen(peerId: string, timeout = 5_000): Promise<boolean> {
    if (this.isOpen(peerId)) return Promise.resolve(true);
    return new Promise((resolve) => {
      const end = window.setTimeout(() => {
        stop();
        resolve(false);
      }, timeout);
      const stop = (): void => {
        window.clearTimeout(end);
        clearInterval(check);
      };
      const check = window.setInterval(() => {
        if (this.isOpen(peerId)) {
          stop();
          resolve(true);
        }
      }, 50);
    });
  }

  async waitBuffer(peerId: string, limit = 8 * 1024 * 1024): Promise<void> {
    if (this.bufferedAmount(peerId) <= limit) return;
    await new Promise<void>((resolve) => {
      const check = window.setInterval(() => {
        if (this.bufferedAmount(peerId) <= 1 * 1024 * 1024) {
          clearInterval(check);
          resolve();
        }
      }, 20);
    });
  }

  close(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    if (peer.heartbeatTimer) {
      window.clearInterval(peer.heartbeatTimer);
      peer.heartbeatTimer = undefined;
    }

    peer.channel?.close();
    peer.pc.close();
    this.peers.delete(peerId);

    // Tests and recovery code can deliberately tear down a channel; make the
    // fallback deterministic even when the browser skips connectionstatechange.
    this.callbacks.closed(peerId);
  }

  private attachChannel(peerId: string, peer: PeerRtc, channel: RTCDataChannel): void {
    peer.channel = channel;
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = 1 * 1024 * 1024;

    const startHeartbeat = (): void => {
      if (peer.heartbeatTimer) window.clearInterval(peer.heartbeatTimer);
      // Cellular NAT mappings expire in ~30s; ping every 15s.
      peer.heartbeatTimer = window.setInterval(() => {
        if (channel.readyState === 'open') {
          try {
            channel.send(JSON.stringify({ v: PROTOCOL_VERSION, type: 'ping', at: Date.now() }));
          } catch {
            /* ignore */
          }
        }
      }, 15_000);
    };

    channel.onopen = (): void => {
      this.callbacks.path(peerId, 'direct');
      startHeartbeat();
    };

    if (channel.readyState === 'open') {
      startHeartbeat();
    }

    channel.onmessage = (event): void => {
      if (typeof event.data === 'string') {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed && typeof parsed === 'object') {
            if (parsed.type === 'ping') {
              channel.send(JSON.stringify({ v: PROTOCOL_VERSION, type: 'pong', at: Date.now() }));
              return;
            }
            if (parsed.type === 'pong') {
              return;
            }
          }
        } catch {
          /* pass non-json through */
        }
        this.callbacks.data(peerId, event.data);
      } else if (event.data instanceof ArrayBuffer) {
        this.callbacks.data(peerId, event.data);
      } else if (event.data instanceof Blob) {
        void event.data.arrayBuffer().then((value) => this.callbacks.data(peerId, value));
      }
    };

    channel.onclose = (): void => {
      if (peer.heartbeatTimer) {
        window.clearInterval(peer.heartbeatTimer);
        peer.heartbeatTimer = undefined;
      }
      this.callbacks.closed(peerId);
    };
  }
}
